import React, { useState, useEffect } from "react";
import { X, Eye, EyeOff } from "lucide-react";

const CompanyEditModel = ({ onClose, onSave, initialData = null, title }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    call_minute_limit: 0,
    status: true,
    username: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isEditMode = Boolean(initialData);

  // Set initial values if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        id :initialData.id ,
        name: initialData.name || "",
        description: initialData.description || "",
        call_minute_limit: initialData.call_minute_limit || 0,
        status: initialData.status !== undefined ? initialData.status : true,
        username: "",
        email: "",
        password: "",
        confirm_password: "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = () => {
    let newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Company Name cannot be empty";
    if (!formData.description.trim())
      newErrors.description = "Description cannot be empty";

    // Only validate these fields in add mode
    if (!isEditMode) {
      if (!formData.username.trim()) newErrors.username = "Username cannot be empty";
      if (!formData.email.trim()) newErrors.email = "Email cannot be empty";
      if (!formData.password) newErrors.password = "Password cannot be empty";
      if (formData.password !== formData.confirm_password)
        newErrors.confirm_password = "Passwords do not match";
    }

    if (isEditMode && formData.call_minute_limit < 0)
      newErrors.call_minute_limit = "Call minute limit cannot be negative";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ id: initialData?.id, ...formData });
    onClose();
  };

  return (
    <div className="add-subparam-overlay">
      <div className="add-subparam-modal">
        {/* Header */}
        <div className="add-subparam-header">
          <h3>{title || (isEditMode ? "Edit Company" : "Add Company")}</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="add-subparam-body">
          {/* Company Name */}
          <label>Company ID</label>
          <input
            type="text"
            name="id"
            className="form-control"
            value={formData.id}
            placeholder=""
            disabled={true}
          />
          {/* Company Name */}
          <label>Company Name</label>
          <input
            type="text"
            name="name"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter company name"
            disabled={isEditMode}
          />
          {errors.name && <p className="edit-error">{errors.name}</p>}

          {/* Company Description */}
          <label style={{ marginTop: "10px" }}>Description</label>
          <textarea
            name="description"
            className="form-control"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter company description"
            rows="3"
          />
          {errors.description && <p className="edit-error">{errors.description}</p>}

          {/* Show these fields only in add mode */}
          {!isEditMode && (
            <>
              <label style={{ marginTop: "10px" }}>Username</label>
              <input
                type="text"
                name="username"
                className="form-control"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter username"
              />
              {errors.username && <p className="edit-error">{errors.username}</p>}

              <label style={{ marginTop: "10px" }}>Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
              />
              {errors.email && <p className="edit-error">{errors.email}</p>}

              <label style={{ marginTop: "10px" }}>Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </span>
              </div>
              {errors.password && <p className="edit-error">{errors.password}</p>}

              <label style={{ marginTop: "10px" }}>Confirm Password</label>
              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  className="form-control"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm password"
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </span>
              </div>
              {errors.confirm_password && (
                <p className="edit-error">{errors.confirm_password}</p>
              )}
            </>
          )}

          {/* Fields only for Edit Mode */}
          {isEditMode && (
            <>
              <label style={{ marginTop: "10px" }}>Call Minute Limit</label>
              <input
                type="number"
                name="call_minute_limit"
                className="form-control"
                value={formData.call_minute_limit}
                onChange={handleChange}
                placeholder="Enter call minute limit"
                min={0}
              />
              {errors.call_minute_limit && (
                <p className="edit-error">{errors.call_minute_limit}</p>
              )}

              <label style={{ marginTop: "10px" }}>
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                />{" "}
                Active
              </label>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="add-subparam-footer">
          <button className="popup-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="popup-add-btn" onClick={handleSave}>
            {isEditMode ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyEditModel;
